import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { Card, Column, DataTable, DataTableFilterMeta, DDCountries, FilterMatchMode, ProgressBar, SCIcon } from '../../Common/Imports';
import { ApiResponse, Countries, ImportData_EXT } from '../../Models/Classes';
import { ExcelExport } from '../../Common/Utility/ExcelExport';

export const DataImportNoISO: React.FC = () => {
    const { t } = useTranslation();
    const [data, setData] = useState<ImportData_EXT[]>([]);
    const AxiosHelper = useAxios();
    const { showToast } = useToast();
    const [CountriesData, setCountriesData] = useState<Countries[] | undefined>();
    const [TableLoader, SetTableLoader] = useState<boolean>(false);
    const [TableFilters, setTableFilters] = useState<DataTableFilterMeta>({
        stakeholderName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        customerReservationCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
        structureName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        structureAddress: { value: null, matchMode: FilterMatchMode.CONTAINS },
        brandName: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });

    const GetData = useCallback(async () => {
        SetTableLoader(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<ImportData_EXT[]>>(`Operative/GetListWithNoISO`);
            if (resp.data.code === 0) {
                const processedData = resp.data.data.map(item => ({
                    ...item,
                    checkInDate: new Date(item.checkInDate),
                    checkOutDate: new Date(item.checkOutDate)
                }));
                setData(processedData);
            }
            const resp2 = await AxiosHelper.get<ApiResponse<Countries[]>>(`Countries/GetList`);
            if (resp2.data.code === 0) {
                setCountriesData(resp2.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            SetTableLoader(false);
        }
    }, [AxiosHelper]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const UpdateRowData = async (data: ImportData_EXT, e: number | undefined) => {
        const _C = CountriesData?.find(m => m.idCountry == e);
        if (_C) {
            data.structureISOCountryCode = _C?.isO2;
            try {
                await AxiosHelper.post(`Imports/SaveSingle`, data);
                showToast('success', 'Success', t("Common.Toasts.Update.Success"));
                GetData();
            } catch (error: unknown) {
                console.log(error);
                showToast('error', 'Error', t("Common.Toasts.Update.Error"));
            }
        }
    }

    const setCountryBody = (data: ImportData_EXT) => {
        return (
            <DDCountries onChange={(e) => UpdateRowData(data, e)} ></DDCountries>
        );
    }

    const HeaderTemplate = (
        <div className="flex justify-content-between align-items-center pt-2 pe-2 ps-2">
            <div className="flex align-items-center">
                <SCIcon Name="Refresh" Pointer OnClick={() => GetData()} />
            </div>
            <div className="flex align-items-center">
            <SCIcon Name="Export" Pointer OnClick={() => ExcelExport(data, TableFilters, "EXPORT_Import")} />
            </div>
        </div>
    );

    return (
        TableLoader ?
            <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
            :
            <Card header={HeaderTemplate}>
                <div className="row">
                    <div className="col">
                        <DataTable
                            value={data}
                            size="small"
                            stripedRows
                            paginator
                            rows={10}
                            onFilter={(e) => setTableFilters(e.filters)}
                            filters={TableFilters}
                            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                            loading={data ? false : true}
                            rowsPerPageOptions={[10, 20, 50]}
                            tableStyle={{ minWidth: '50rem' }}>
                            <Column
                                body={(e: ImportData_EXT) => setCountryBody(e)}
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="stakeholderName"
                                header={t("Imports.DataImportNoISO.List.StakeholderName")}
                                sortable
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="customerReservationCode"
                                header={t("Imports.DataImportNoISO.List.ReservationCode")}
                                sortable
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="structureName"
                                header={t("Imports.DataImportNoISO.List.StructureName")}
                                sortable
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="structureAddress"
                                header={t("Imports.DataImportNoISO.List.StructureAddress")}
                                sortable
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="structureCity"
                                header={t("Imports.DataImportNoISO.List.StructureCity")}
                                sortable
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="structureCountry"
                                header={t("Imports.DataImportNoISO.List.StructureCountry")}
                                sortable
                                bodyClassName="table-cell-nowrap" />
                            {/* The list no longer means "the ISO field is empty", so the
                                reason a record appears is no longer uniform. Showing the
                                value it actually carries lets an operator tell a record
                                with nothing to work from from one carrying a value that
                                simply is not a country code. */}
                            <Column
                                filter
                                field="structureISOCountryCode"
                                header={t("Imports.DataImportNoISO.List.StructureISOCountryCode")}
                                sortable
                                bodyClassName="table-cell-nowrap" />
                            {/* A record with coordinates may still be resolved by the import
                                without anyone editing it; one without them cannot. */}
                            <Column
                                header={t("Imports.DataImportNoISO.List.HasCoordinates")}
                                body={(e: ImportData_EXT) =>
                                    e.structureLatitude != null && e.structureLongitude != null
                                        ? t("Imports.DataImportNoISO.List.HasCoordinatesYes")
                                        : t("Imports.DataImportNoISO.List.HasCoordinatesNo")}
                                bodyClassName="table-cell-nowrap" />
                        </DataTable>
                    </div>
                </div>
            </Card>
    );
};

export default DataImportNoISO;
